// UX analytics + crash reporting wiring for accutrax-front.
//   crash upload: always on (support.accutrax.id/api/client-crashes)
//   ux events:    only when the tenant's API says ux_analytics is on (GET /api/ux/config, or the
//                 X-Ux-Analytics header); off / 404 -> nothing is collected, only the breadcrumb ring.
// Contract: ux-analytics-spec.md sections 1-2 and the Accutrax client contract (accutrax-api).
import axios from 'axios';
import { watch } from 'vue';
import Validator from 'devextreme/ui/validator';
import DataGrid from 'devextreme/ui/data_grid';
import { createTracker, createSession, screenName } from './tracker.mjs';
import { createCrashReporter } from './crash-reporter.js';
import { pathTemplate, tenantFromHost } from './scrub.mjs';

export const APP = 'accutrax-front';
export const APP_VERSION = process.env.VUE_APP_VERSION || '0.0.0';
export const APP_VERSION_CODE = Number(process.env.VUE_APP_VERSION_CODE) || 0;
const IS_PROD = process.env.NODE_ENV === 'production';
const SLOW_MS = 2000;
const FLUSH_EVERY_MS = 30000;

// endpoint verb -> action name (action_done)
const ACTIONS = {
  simpan: 'simpan',
  baru: 'simpan',
  update: 'simpan',
  save: 'simpan',
  save_setting: 'simpan',
  hapus: 'hapus',
  upload_hapus: 'hapus',
  upload: 'upload',
  bayar: 'bayar',
  posting: 'posting',
  batal: 'batal',
  approve: 'approve',
  import: 'import',
};

let ux = null; // singleton: { tracker, crash, ... }

function sessionStorageOrNull() {
  try {
    sessionStorage.getItem('x');
    return sessionStorage;
  } catch {
    return null;
  }
}

function storageOrNull() {
  try {
    localStorage.getItem('x');
    return localStorage;
  } catch {
    return null;
  }
}

export function useUx() {
  return ux;
}

// Safe no-op wrappers for components.
export function trackUx(event, opts) {
  try {
    return ux?.tracker.track(event, opts);
  } catch {
    return null;
  }
}

export function actionCount() {
  return ux ? ux.actions : 0;
}

export function installUx(app, router, store) {
  if (ux) return ux;
  // Route name, else the route path as a template (never blank: screenName falls back to "(tanpa layar)").
  const getScreen = () => {
    try {
      const r = router.currentRoute.value;
      return screenName(r?.name, pathTemplate(r?.path || ''), pathTemplate((location.hash || '').replace(/^#/, '')));
    } catch {
      return screenName();
    }
  };
  const getTenant = () => {
    try {
      return tenantFromHost(new URL(store.server).hostname) || tenantFromHost(location.hostname);
    } catch {
      return null;
    }
  };

  async function send(events) {
    const res = await fetch(`${store.baseApiUrl}ux/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', token: store.token },
      body: JSON.stringify({ events }),
      keepalive: true,
    });
    return { status: res.status, ux: (res.headers.get('X-Ux-Analytics') || '').toLowerCase() || null };
  }

  // new session after 30 minutes idle and at login / logout (token change), see createSession
  const session = createSession({ storage: sessionStorageOrNull() });
  const tracker = createTracker({
    storage: storageOrNull(),
    send,
    app: APP,
    appVersion: APP_VERSION,
    sessionId: () => session.touch(),
    getScreen,
  });

  // Dev server / e2e: no crash uploads to the real platform unless a test opts in.
  const crashEnabled = () => IS_PROD || !!window.__UX_CRASH_FORCE;
  const crash = createCrashReporter({
    app: APP,
    appVersionName: APP_VERSION,
    appVersionCode: APP_VERSION_CODE,
    getTenant,
    getScreen,
    getBreadcrumbs: () => tracker.breadcrumbs(),
    enabled: crashEnabled,
  });

  ux = { tracker, crash, session, actions: 0 };
  window.__accutraxUx = ux; // for support/debugging from the console

  crash.install(app);

  // ---- flag: GET /api/ux/config after login / restore ----
  let configFor = null;
  async function loadConfig() {
    const token = store.token;
    if (!token || configFor === token) return;
    configFor = token;
    try {
      const res = await fetch(`${store.baseApiUrl}ux/config`, { headers: { token, Accept: 'application/json' } });
      if (res.status === 404) return tracker.setEnabled(false);
      if (!res.ok) {
        configFor = null; // retry on the next tick
        return;
      }
      const body = await res.json().catch(() => null);
      tracker.resume();
      tracker.setEnabled(!!body?.data?.enabled);
      if (tracker.enabled) tracker.flush();
    } catch {
      configFor = null;
    }
  }
  watch(
    () => store.token,
    (t, old) => {
      // login (a new token) or logout (token gone): a new UX session; the first run (restore) keeps it
      if (old !== undefined && t !== old) session.rotate();
      if (t) loadConfig();
    },
    { immediate: true }
  );

  setInterval(() => {
    if (tracker.enabled === null) loadConfig();
    else if (store.token) tracker.flush();
  }, FLUSH_EVERY_MS);
  const flushNow = () => {
    if (store.token) tracker.flush(100); // keepalive bodies are capped at 64 KB
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow();
  });
  window.addEventListener('pagehide', flushNow);

  // ---- routes: screen_view, slow route loads, print (cetak) routes ----
  let navStart = 0;
  let screenSince = Date.now();
  router.beforeEach(() => {
    navStart = performance.now();
  });
  router.afterEach((to, from, failure) => {
    try {
      if (failure) return;
      const name = screenName(to.name, pathTemplate(to.path || ''));
      const prevName = from.name || from.matched?.length ? screenName(from.name, pathTemplate(from.path || '')) : '';
      const t = Date.now();
      tracker.track('screen_view', {
        screen: name,
        duration_ms: prevName ? t - screenSince : null,
        meta: { from: prevName || null },
      });
      screenSince = t;
      const loadMs = performance.now() - navStart;
      if (loadMs > SLOW_MS) tracker.track('slow', { screen: name, duration_ms: loadMs, meta: { what: `route:${name}` } });
      if (/_cetak$/.test(name)) {
        ux.actions++;
        tracker.track('action_done', { screen: name, meta: { action: 'cetak', entity: name.replace(/_cetak$/, '') } });
      }
    } catch {
      /* never throw */
    }
  });

  // ---- API: slow requests, successful save-type calls, the X-Ux-Analytics header ----
  axios.interceptors.request.use((config) => {
    try {
      config.__uxStart = performance.now();
    } catch {
      /* ignore */
    }
    return config;
  });
  const onDone = (resp, config) => {
    try {
      if (!config) return;
      const what = pathTemplate(config.url);
      const ms = config.__uxStart != null ? performance.now() - config.__uxStart : 0;
      if (ms > SLOW_MS) tracker.track('slow', { duration_ms: ms, meta: { what } });
      const hdr = resp?.headers?.['x-ux-analytics'];
      if (hdr === 'off') tracker.setEnabled(false);
      else if (hdr === 'on' && tracker.enabled === null) tracker.setEnabled(true);
      if (!resp || resp.status >= 300 || resp.data?.ok === false) return;
      if ((config.method || '').toLowerCase() !== 'post') return;
      const segs = what.split('/').filter((s) => s && s !== ':id');
      const verb = segs[segs.length - 1];
      const action = ACTIONS[verb];
      if (!action || segs.length < 2) return;
      ux.actions++;
      tracker.track('action_done', { meta: { action, entity: segs.slice(0, -1).join('/') } });
    } catch {
      /* never throw */
    }
  };
  axios.interceptors.response.use(
    (resp) => {
      onDone(resp, resp?.config);
      return resp;
    },
    (err) => {
      onDone(err?.response, err?.config);
      return Promise.reject(err);
    }
  );

  // ---- validation errors: every dxValidator (dxForm items included) ----
  const lastValidation = new Map();
  try {
    Validator.defaultOptions({
      options: {
        onValidated(e) {
          try {
            if (e.isValid !== false) return;
            const raw = e.validator?.element?.();
            const el = raw?.nodeType ? raw : raw?.[0];
            const input = el?.querySelector?.('input[name], textarea[name]');
            const field = (input && input.getAttribute('name')) || e.validator?.option?.('name') || '';
            const form = getScreen();
            const key = `${form}|${field}`;
            const now = Date.now();
            if (now - (lastValidation.get(key) || 0) < 5000) return;
            lastValidation.set(key, now);
            tracker.track('validation_error', { meta: { form, field: field || null } });
          } catch {
            /* ignore */
          }
        },
      },
    });
  } catch {
    /* ignore */
  }

  // ---- empty searches: grid search panels (all grids) ----
  try {
    DataGrid.defaultOptions({
      options: {
        onContentReady(e) {
          try {
            const term = e.component.option('searchPanel.text');
            if (!term || e.component.totalCount() !== 0) return;
            searchEmpty(`grid:${getScreen()}`, String(term).length);
          } catch {
            /* ignore */
          }
        },
      },
    });
  } catch {
    /* ignore */
  }

  return ux;
}

// Lookup / select-box searches through Helper's CustomStore (loadOptions.searchValue).
const lastEmpty = new Map();
export function searchEmpty(where, termLen) {
  try {
    if (!ux || !termLen) return;
    const key = `${where}|${termLen}`;
    const now = Date.now();
    if (now - (lastEmpty.get(key) || 0) < 3000) return;
    lastEmpty.set(key, now);
    ux.tracker.track('search_empty', { meta: { where: String(where).slice(0, 48), term_len: termLen } });
  } catch {
    /* ignore */
  }
}

export function noteLookupResult(path, loadOptions, data) {
  try {
    const term = loadOptions?.searchValue;
    if (term == null || term === '') return;
    const rows = Array.isArray(data) ? data.length : Array.isArray(data?.data) ? data.data.length : data?.totalCount;
    if (rows === 0) searchEmpty(`lookup:${pathTemplate(path).replace(/\/daftar$/, '')}`, String(term).length);
  } catch {
    /* ignore */
  }
}
